/**
 * MyWorkProvider — THE single source for My Work data. One App-level fetch
 * feeds the nav badge, the hub page, and anything else that asks, so every
 * number on screen comes from the same response and can never disagree.
 * My Work is deliberately account-wide (never location-scoped): it answers
 * "what is waiting on YOU", wherever that work lives.
 *
 * Freshness model, in trust order:
 *  1. actions refetch immediately (the acting screen calls refresh()),
 *  2. the /me/work/events SSE ping (useMyWorkSSE) triggers a debounced
 *     refetch — a NUDGE only: a dropped broadcast loses pings,
 *  3. refetch on tab focus and a 90s interval while visible, so counters
 *     are never more than seconds-to-a-minute stale even with SSE down.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { myWorkService } from '@turn-connect/shared/web';
import { useMyWorkSSE, type MyWorkResponse } from '@turn-connect/shared';
import { getApiBaseUrl } from '../utils/config';

const POLL_MS = 90_000;

interface MyWorkState {
  data: MyWorkResponse | null;
  loading: boolean;
  refresh: () => Promise<void>;
  count: number;
  /** The live channel is open (for the page's live line). */
  connected: boolean;
  /** When the data last arrived (ms since epoch), null before the first load. */
  updatedAt: number | null;
}

const MyWorkContext = createContext<MyWorkState | null>(null);

function useMyWorkSource(accountId: string | null | undefined): MyWorkState {
  const [data, setData] = useState<MyWorkResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const res = await myWorkService.getMyWork({ account_id: accountId });
      if (res.success && res.data) {
        setData(res.data);
        setUpdatedAt(Date.now());
      }
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Focus + visible-interval refetch (the honest fallback).
  useEffect(() => {
    if (!accountId) return undefined;
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, POLL_MS);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
    };
  }, [refresh, accountId]);

  // Live nudge (cookie auth rides along); debounced inside the hook.
  const { isConnected } = useMyWorkSSE({ baseUrl: getApiBaseUrl(), enabled: !!accountId, onNudge: () => void refresh() });

  return { data, loading, refresh, count: data?.counts?.total ?? 0, connected: isConnected, updatedAt };
}

export const MyWorkProvider: React.FC<{ accountId: string | null | undefined; children: React.ReactNode }> = ({ accountId, children }) => {
  const state = useMyWorkSource(accountId);
  return <MyWorkContext.Provider value={state}>{children}</MyWorkContext.Provider>;
};

export function useMyWork(): MyWorkState {
  const ctx = useContext(MyWorkContext);
  if (!ctx) throw new Error('useMyWork must be used inside MyWorkProvider');
  return ctx;
}
